import { CombinedGraphQLErrors } from "@apollo/client/errors";
import { useComputed, useSnackbar } from "@homebound/beam";
import omit from "lodash/omit";
import { type PropsWithChildren, createContext, useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { type AiFieldProposalFormValue } from "src/components/formFields/ai/utils/aiFieldProposalForm";
import {
  initializeMaterialLeadTimeConfigFields,
  maybeMaterialLeadTimeConfig,
  maybeOptionPricingConfig,
} from "src/routes/admin/utils";
import {
  type MaterialListing_DetailFragment,
  type MaterialListing_DetailVariantFragment,
  useDeleteMaterialVariantMutation,
  useSaveMaterialListingMutation,
} from "src/routes/libraries/material-catalog/components/materialListingDetail.generated";
import {
  type MaterialListingFormState,
  type MaterialVariantFormValue,
  getPriceEstimateText,
  materialListingFormConfig,
  saveMaterialListingProposalsInput,
  saveMaterialListingVariantInput,
} from "src/routes/libraries/material-catalog/components/materialListingForm";
import { useMaterialListingPageQuery } from "src/routes/libraries/material-catalog/material-listing-page/MaterialListingPage.generated";
import { MaterialCatalogListingsDocument } from "src/routes/libraries/material-catalog/MaterialCatalog.generated";
import { noop } from "src/utils";
import { ObjectState, useFormState } from "src/utils/formState";

type MaterialListingContextProps = {
  listing: MaterialListing_DetailFragment | undefined;
  /**
   * The listing's variants in *display* order — the default first, then by featured image.
   *
   * Ordered here rather than in the thumbnail strip so the strip leads with the default even when
   * that variant is not the first form row.
   */
  variants: MaterialListing_DetailVariantFragment[];
  selectedVariant: MaterialListing_DetailVariantFragment | undefined;
  /**
   * The selected variant's row in `formState.variants` — i.e. the editable half of `selectedVariant`.
   *
   * Derived here rather than in each section, because every one of them needs it and the lookup has to be
   * a `useComputed` to stay reactive as rows are added.
   */
  selectedVariantForm: ObjectState<MaterialVariantFormValue> | undefined;
  setSelectedVariant: (id: string | undefined) => void;
  /**
   * True while the URL is in create mode (`?createNew=1`) and the user is filling an unsaved row.
   */
  isEditingPendingVariant: boolean;
  /** Appends an empty variant row and enters create mode; a second click re-selects the existing unsaved row. */
  addVariant: VoidFunction;
  /**
   * Clones the selected variant into a new unsaved row (no id/code/images) and enters create mode.
   * Replaces any existing pending row so only one unsaved variant exists at a time.
   */
  copyVariant: VoidFunction;
  /** Selects the unsaved row (re-enters create mode) without adding another row. */
  selectPendingVariant: VoidFunction;
  /** Drops the unsaved row and returns to a saved variant selection. */
  discardPendingVariant: VoidFunction;
  /** Hard deletes a variant outright, rather than archiving it the way the Status field does. */
  deleteVariant: (id: string) => Promise<void>;
  formState: MaterialListingFormState;
  loading: boolean;
  /** Resolves false when the server rejected the save, so callers can avoid navigating away from the edits. */
  save: () => Promise<boolean>;
};

const MaterialListingContext = createContext<MaterialListingContextProps>({
  listing: undefined,
  variants: [],
  selectedVariant: undefined,
  selectedVariantForm: undefined,
  setSelectedVariant: () => {},
  isEditingPendingVariant: false,
  addVariant: () => {},
  copyVariant: () => {},
  selectPendingVariant: () => {},
  discardPendingVariant: () => {},
  deleteVariant: () => Promise.resolve(),
  loading: false,
  formState: undefined!,
  save: () => Promise.resolve(true),
});
type MaterialListingProviderProps = PropsWithChildren<{
  listing: MaterialListing_DetailFragment | undefined;
  loading: boolean;
  /** Controlled by the caller so the page can source it from the URL (`?mv=`). */
  selectedVariantId: string | undefined;
  /** Controlled by the caller so the page can source it from the URL (`?createNew=1`). */
  createNew: boolean;
  /** Listing-level AI field proposals, which the form keys to its fields by `fieldName`. */
  listingFieldProposals?: AiFieldProposalFormValue[];
  /**
   * Variant-level AI field proposals, keyed by variant id.
   */
  variantFieldProposals?: Record<string, AiFieldProposalFormValue[]>;
  onSelectVariant: (id: string | undefined) => void;
  onCreateNew: VoidFunction;
  /**
   * Called when `deleteVariant` took the whole listing with it, i.e. the reviewer deleted its last
   * variant. There is nothing left for this provider to show, so the caller has to move them on.
   */
  onListingDeleted?: VoidFunction;
}>;

/**
 * Owns the listing form state and save.
 *
 * The form spans the *whole* listing (`variants` is a form list), so every variant's edits are held
 * at once and switching the selected variant never re-inits or drops changes.
 */
function MaterialListingProvider(props: MaterialListingProviderProps) {
  const {
    children,
    listing,
    loading,
    selectedVariantId,
    createNew,
    listingFieldProposals,
    variantFieldProposals,
    onSelectVariant,
    onCreateNew,
    onListingDeleted,
  } = props;
  const [saveMaterialListing, { loading: saving }] = useSaveMaterialListingMutation({ onError: noop });
  const [deleteMaterialVariant, { loading: deleting }] = useDeleteMaterialVariantMutation();
  const { triggerNotice } = useSnackbar();

  const defaultVariantId = listing?.defaultVariant?.id;
  const variants = useMemo(
    () =>
      (listing?.materialVariants ?? []).sortBy((mv) => [
        mv.id === defaultVariantId ? 0 : 1,
        mv.featuredImage?.sortOrder ?? 0,
      ]),
    [listing?.materialVariants, defaultVariantId],
  );
  const isEditingPendingVariant = createNew;
  const selectedVariant = isEditingPendingVariant ? undefined : variants.find((mv) => mv.id === selectedVariantId);

  const formConfig = useMemo(() => materialListingFormConfig({ canEditItem: true, canEditType: true }), []);

  const formState = useFormState({
    config: formConfig,
    init: {
      input: listing,
      map: (listing) => ({
        id: listing?.id,
        name: listing?.name,
        description: listing?.description,
        type: listing?.type.code,
        itemId: listing?.item.id,
        brandId: listing?.brand?.id,
        designNote: listing?.designNote,
        defaultVariantId: listing?.defaultVariant?.id,
        variants: listing?.materialVariants.map((mv) => ({
          id: mv.id,
          code: mv.code,
          canEdit: mv.canEdit,
          codeOverride: mv.codeOverride,
          modelNumber: mv.modelNumber,
          manufacturerUrl: mv.manufacturerUrl,
          deletedAt: mv.deletedAt,
          designFeedback: mv.designFeedback,
          mavIds: mv.materialAttributeValues.map((mav) => mav.id),
          priceEstimate: getPriceEstimateText(mv, "None added"),
          images: mv.images.map(({ id, sortOrder, asset }) => ({
            id,
            asset: { id: asset?.id, fileName: asset?.fileName, contentType: asset?.contentType },
            attachmentUrl: asset?.attachmentUrl,
            downloadUrl: asset?.downloadUrl,
            sortOrder,
          })),
          tags: mv.tags.map((t) => t.id),
          fieldProposals: variantFieldProposals?.[mv.id] ?? [],
          components: mv.components.map(({ id, qualifier, component }) => ({
            id,
            qualifier,
            componentId: component.id,
            componentItemId: component.listing.item.id,
            componentValues: component.materialAttributeValues.map((mav) => mav.id),
          })),
        })),
        fieldProposals: listingFieldProposals ?? [],
        optionPricingConfig: listing.optionPricingConfig ?? {},
        materialLeadTimeConfig: { ...initializeMaterialLeadTimeConfigFields(listing.materialLeadTimeConfig) },
      }),
    },
    loading,
  });

  // Deep-link / refresh with `?createNew=1`: ensure an empty form row exists once the listing is loaded.
  // Skip while saving — cache re-init assigns the pending row an id mid-save, and create mode would
  // otherwise add a second blank row.
  useEffect(() => {
    if (!createNew || loading || saving || !listing) return;
    if (!formState.variants.rows.some((v) => !v.id.value)) {
      formState.variants.add({});
    }
  }, [createNew, loading, saving, listing, formState]);

  const selectedVariantForm = useComputed(() => {
    if (isEditingPendingVariant) {
      return formState.variants.rows.find((v) => !v.id.value);
    }
    return formState.variants.rows.find((v) => v.id.value === selectedVariantId);
  }, [formState, selectedVariantId, isEditingPendingVariant]);

  const setSelectedVariant = useCallback(
    (id: string | undefined) => {
      onSelectVariant(id);
    },
    [onSelectVariant],
  );

  const addVariant = useCallback(() => {
    if (!formState.variants.rows.some((v) => !v.id.value)) {
      formState.variants.add({});
    }
    onCreateNew();
  }, [formState, onCreateNew]);

  const copyVariant = useCallback(() => {
    const source = isEditingPendingVariant
      ? formState.variants.rows.find((v) => !v.id.value)
      : formState.variants.rows.find((v) => v.id.value === selectedVariantId);
    if (!source) return;
    // Snapshot before removing any pending row (source may be that row).
    const cloned = omit(source.value, "id", "code", "images", "canEdit", "priceEstimate", "fieldProposals");
    const pending = formState.variants.rows.find((v) => !v.id.value);
    if (pending) formState.variants.remove(pending.value);
    formState.variants.add(cloned);
    onCreateNew();
  }, [formState, isEditingPendingVariant, selectedVariantId, onCreateNew]);

  const selectPendingVariant = useCallback(() => onCreateNew(), [onCreateNew]);
  const discardPendingVariant = useCallback(() => {
    const pending = formState.variants.rows.find((v) => !v.id.value);
    if (pending) formState.variants.remove(pending.value);
    onSelectVariant(defaultVariantId ?? variants.first?.id);
  }, [formState, onSelectVariant, defaultVariantId, variants]);

  const deleteVariant = useCallback(
    async (id: string) => {
      // Grab the row before the delete: the mutation's cache write re-inits the form while we await.
      const variantRow = formState.variants.rows.find((v) => v.id.value === id);
      const { data } = await deleteMaterialVariant({
        variables: { input: { id } },
        refetchQueries: [MaterialCatalogListingsDocument],
      });

      // No listing back means that was its last variant, so the listing went too.
      const remaining = data.deleteMaterialVariant.listing;
      if (!remaining) return onListingDeleted?.();

      if (variantRow) formState.variants.remove(variantRow.value);
      // Hand-off to the next default variant
      const nextId = remaining.defaultVariant?.id ?? remaining.materialVariants.first?.id;
      if (formState.defaultVariantId.value === id) formState.defaultVariantId.set(nextId);
      if (selectedVariantId === id) onSelectVariant(nextId);
    },
    [deleteMaterialVariant, formState, selectedVariantId, onSelectVariant, onListingDeleted],
  );

  const save = useCallback(async () => {
    // Nothing to persist is a success as far as callers are concerned.
    if (!formState.dirty) return true;

    const priorIds = new Set(formState.variants.rows.map((v) => v.id.value).compact());
    const wasPending = isEditingPendingVariant && formState.variants.rows.some((v) => !v.id.value);
    const {
      variants: changedVariants,
      // Form-only bookkeeping: the input takes these as `proposals`, so they must not ride in `others`.
      fieldProposals: _changedProposals,
      optionPricingConfig,
      materialLeadTimeConfig,
      ...others
    } = formState.changedValue;
    const proposals = saveMaterialListingProposalsInput(formState);

    // `saveMaterialListingVariantInput` wants both the row's sparse `changedValue` and its full `value`,
    // so pair each changed row back up with its `ObjectState`. A pending (unsaved) variant has no id to
    // match on, so it is found as "the row without one", and we send its full value rather than the
    // sparse changes — the backend is creating it from scratch.
    const variantInputs = changedVariants?.map((changed) => {
      const row = changed.id
        ? formState.variants.rows.find((r) => r.id.value === changed.id)
        : formState.variants.rows.find((r) => !r.id.value);
      const source = changed.id ? changed : (row?.value ?? changed);
      return saveMaterialListingVariantInput(source, row?.value);
    });

    const result = await saveMaterialListing({
      errorPolicy: "all", // Return errors so this flow can replace the default error banner with a tailored message.
      variables: {
        input: {
          ...others,
          variants: variantInputs,
          ...(proposals.nonEmpty ? { proposals } : {}),
          ...maybeOptionPricingConfig({ ...formState.optionPricingConfig.value }),
          ...maybeMaterialLeadTimeConfig({ ...formState.materialLeadTimeConfig.value }),
        },
      },
      refetchQueries: [MaterialCatalogListingsDocument],
    }).catch(() => undefined);
    const { data, error } = result ?? {};

    if (CombinedGraphQLErrors.is(error) && error.errors.nonEmpty) {
      const mvUniquenessError = error.errors.some(
        (e) => e.message.includes("have the same attributes") || e.message.includes("material_variants_code_key"),
      );

      triggerNotice({
        message: mvUniquenessError
          ? "This material conflicts with an existing material. Please add or alter it's attributes to make it unique"
          : error.errors.map((e) => e.message).join(", "),
        icon: "error",
      });

      return false;
    }

    const savedMaterial = data?.saveMaterialListing;

    // No data and no `CombinedGraphQLErrors` means a transport-level failure.
    // Not likely to happen, but want a guard against it just in case.
    if (!savedMaterial) {
      triggerNotice({ message: "Something went wrong saving this material. Please try again.", icon: "error" });
      return false;
    }

    // Apply the mutation result so server-assigned `mac:` ids land on the form before the next
    // edit. Otherwise a dirty-list refresh can leave the just-saved row without its id, and the
    // following exhaustive components save drops it.
    formState.update(savedMaterial);
    triggerNotice({ message: `Material saved successfully`, icon: "success" });
    if (wasPending) {
      // Drop any blank row the create-mode effect may have added mid-save.
      formState.variants.rows.filter((v) => !v.id.value).forEach((v) => formState.variants.remove(v.value));
      // Select the newly created variant (fallback: last/appended row).
      const addedId = savedMaterial.materialVariants.find((v) => !priorIds.has(v.id))?.id;
      setSelectedVariant(addedId ?? savedMaterial.materialVariants.last?.id);
    }
    return true;
  }, [formState, saveMaterialListing, triggerNotice, isEditingPendingVariant, setSelectedVariant]);

  return (
    <MaterialListingContext.Provider
      value={{
        listing,
        variants,
        selectedVariant,
        selectedVariantForm,
        setSelectedVariant,
        isEditingPendingVariant,
        addVariant,
        copyVariant,
        selectPendingVariant,
        discardPendingVariant,
        deleteVariant,
        formState,
        loading: loading || saving || deleting,
        save,
      }}
    >
      {children}
    </MaterialListingContext.Provider>
  );
}

type MaterialListingByListingIdProviderProps = PropsWithChildren<{
  listingId: string;
  listingFieldProposals?: AiFieldProposalFormValue[];
  variantFieldProposals?: Record<string, AiFieldProposalFormValue[]>;
  selectedVariantId: string | undefined;
  createNew: boolean;
  onSelectVariant: (id: string | undefined) => void;
  onCreateNew: VoidFunction;
  onListingDeleted?: VoidFunction;
  /** Called once listing data is available so the page can canonicalize the URL. */
  onListingLoaded?: (listing: MaterialListing_DetailFragment) => void;
}>;

/**
 * Detail-page entry point: loads the listing directly and takes its selection from the caller
 * (the URL). Because `listingId` is the only variable, switching variants leaves the query — and so
 * the form — untouched.
 */
export function MaterialListingByListingIdProvider(props: MaterialListingByListingIdProviderProps) {
  const {
    children,
    listingId,
    listingFieldProposals,
    variantFieldProposals,
    selectedVariantId,
    createNew,
    onSelectVariant,
    onCreateNew,
    onListingDeleted,
    onListingLoaded,
  } = props;
  const { data, loading, startPolling, stopPolling } = useMaterialListingPageQuery({
    variables: { listingId },
    fetchPolicy: "network-only",
    nextFetchPolicy: "network-only",
  });
  const listing = data?.materialListings.entities.first;

  useAwaitVariantImages(listing, variantFieldProposals, startPolling, stopPolling);

  useEffect(() => {
    if (listing) onListingLoaded?.(listing);
  }, [listing, onListingLoaded]);

  return (
    <MaterialListingProvider
      listing={listing}
      loading={loading}
      listingFieldProposals={listingFieldProposals}
      variantFieldProposals={variantFieldProposals}
      selectedVariantId={selectedVariantId}
      createNew={createNew}
      onSelectVariant={onSelectVariant}
      onCreateNew={onCreateNew}
      onListingDeleted={onListingDeleted}
    >
      {children}
    </MaterialListingProvider>
  );
}

export function useMaterialListingContext() {
  return useContext(MaterialListingContext);
}

/** How often to re-check for a variant image that is still downloading. */
const variantImagePollIntervalMs = 10_000;
/** Give up after this long, so a download that failed outright doesn't poll forever. */
const variantImagePollTimeoutMs = 2 * 60_000;

/**
 * Re-fetches the listing until each imported photo has landed.
 *
 * An `imageUrl` field proposal is the record of "the agent found a photo for this variant"; the
 * listing says whether it has arrived yet. Together that keeps us from polling variants that simply
 * have no photo. {@link variantImagePollTimeoutMs} is the backstop if a download fails outright.
 *
 * Nothing else needs to change for the thumbnails to update: `useFormState` keeps a stable form
 * across query refreshes and `set(..., { refreshing: true })`s the new value in, which leaves any
 * field the user is editing alone. So the placeholder swaps itself out as each image lands.
 */
function useAwaitVariantImages(
  listing: MaterialListing_DetailFragment | undefined,
  variantFieldProposals: Record<string, AiFieldProposalFormValue[]> | undefined,
  startPolling: (ms: number) => void,
  stopPolling: () => void,
) {
  const isWaiting = !!listing?.materialVariants.some(
    (mv) => mv.images.isEmpty && variantFieldProposals?.[mv.id]?.some((p) => p.fieldName === "imageUrl"),
  );
  const deadline = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (!isWaiting) {
      stopPolling();
      return;
    }
    deadline.current ??= Date.now() + variantImagePollTimeoutMs;
    const remaining = deadline.current - Date.now();
    if (remaining <= 0) {
      stopPolling();
      return;
    }
    startPolling(variantImagePollIntervalMs);
    const timer = setTimeout(stopPolling, remaining);
    return () => clearTimeout(timer);
  }, [isWaiting, startPolling, stopPolling]);
}
